// Generated from scripts/volume-pricing.ts. Rebuild with pnpm scripts:build.
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';
function toInteger(value) {
    if (value === null || value === undefined || value === '')
        return undefined;
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
}
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
/**
 * The quantity of the variant the cart would hold after an add: what it holds now plus the quantity in the input.
 * Shopify applies a price break to the quantity of a cart line, so this is the quantity a break is reached with.
 * Undefined when the element is not in a product form.
 */
function quantityAfterAdd(element) {
    const input = element.closest('product-form-component')?.querySelector('input[name="quantity"]');
    if (!(input instanceof HTMLInputElement))
        return undefined;
    return (toInteger(input.getAttribute('data-cart-quantity')) ?? 0) + (toInteger(input.value) ?? 1);
}
/**
 * Calls `update` whenever the quantity the element reads can have changed: the stepper of its own form changed, or a
 * cart update landed. The cart update waits one microtask after its promise settles, so the product form has written
 * the cart quantity it read from the same response before the element reads it.
 */
function followQuantity(element, update, signal) {
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
        if (event.target instanceof Node && element.closest('product-form-component')?.contains(event.target))
            update();
    }, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
        if (!('promise' in event) || !(event.promise instanceof Promise))
            return;
        event.promise.then(() => queueMicrotask(update)).catch(() => undefined);
    }, { signal });
}
/**
 * The table of a variant's volume pricing. Shopify renders the rows; this element marks the price break the quantity
 * in the form reaches (`aria-current` on its row, which shows a check) and keeps it marked as the shopper changes the
 * quantity or the cart changes. The first three rows show; the rest sit behind a button, except the marked row, which
 * always shows.
 *
 * Contract kept from the inherited module: the `volume-pricing` element, its `volumePricing` and
 * `volumePricingToggle` refs and the `toggleExpanded` method bound with `on:click`. The `volume-pricing--expanded`
 * class retired: the button's `aria-expanded` is the state now.
 */
class VolumePricingComponent extends Component {
    #lifetime;
    #expanded = false;
    connectedCallback() {
        super.connectedCallback();
        this.#lifetime?.abort();
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        followQuantity(this, () => this.update(), lifetime.signal);
        this.update();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
    }
    updatedCallback() {
        super.updatedCallback();
        this.update();
    }
    /** Shows or hides the rows past the third. */
    toggleExpanded() {
        this.#expanded = !this.#expanded;
        this.update();
    }
    /** Marks the row the form's quantity reaches and applies the expanded state to the rows and the button. */
    update() {
        const rows = Array.from(this.querySelectorAll('tr[data-quantity]'));
        const quantity = quantityAfterAdd(this);
        let active;
        if (quantity !== undefined) {
            let activeTier = -1;
            for (const row of rows) {
                const tier = toInteger(row.dataset.quantity);
                if (tier !== undefined && tier <= quantity && tier >= activeTier) {
                    active = row;
                    activeTier = tier;
                }
            }
            for (const row of rows) {
                if (row === active)
                    row.setAttribute('aria-current', 'true');
                else
                    row.removeAttribute('aria-current');
            }
        }
        for (const row of rows) {
            if (row.hasAttribute('data-extra'))
                row.hidden = !this.#expanded && !row.hasAttribute('aria-current');
        }
        const toggle = this.refs.volumePricingToggle;
        if (toggle instanceof HTMLElement) {
            toggle.setAttribute('aria-expanded', String(this.#expanded));
            for (const label of toggle.querySelectorAll('[data-when]')) {
                label.hidden = (label.dataset.when === 'expanded') !== this.#expanded;
            }
        }
    }
}
if (!customElements.get('volume-pricing')) {
    customElements.define('volume-pricing', VolumePricingComponent);
}
function isPriceTier(value) {
    return isRecord(value) && typeof value.quantity === 'number' && typeof value.price === 'string';
}
/** The tiers an element describes with its data attributes, highest quantity first. Read on every update, because a render for another variant replaces the attributes. */
function readTiers(element) {
    const tiers = [];
    const { variantPrice, minQuantity, priceBreaks } = element.dataset;
    if (variantPrice)
        tiers.push({ quantity: toInteger(minQuantity) ?? 1, price: variantPrice });
    if (priceBreaks) {
        try {
            const parsed = JSON.parse(priceBreaks);
            if (Array.isArray(parsed))
                tiers.push(...parsed.filter(isPriceTier));
        }
        catch {
            // A broken attribute leaves the base tier, which is true for any quantity.
        }
    }
    return tiers.sort((a, b) => b.quantity - a.quantity);
}
/**
 * The price per item next to the stepper: "at $9.50/ea", for the price break the quantity in the form reaches. It
 * updates as the quantity changes, from the break prices Shopify rendered, formatted by Shopify.
 *
 * Contract kept from the inherited module: the `price-per-item` element, the `pricePerItemText` ref, the `data-*`
 * attributes that carry the tiers and the `updatePriceDisplay` method.
 */
class PricePerItemComponent extends Component {
    #lifetime;
    connectedCallback() {
        super.connectedCallback();
        this.#lifetime?.abort();
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        followQuantity(this, () => this.updatePriceDisplay(), lifetime.signal);
        this.updatePriceDisplay();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
    }
    updatedCallback() {
        super.updatedCallback();
        this.updatePriceDisplay();
    }
    updatePriceDisplay() {
        const text = this.refs.pricePerItemText;
        const quantity = quantityAfterAdd(this);
        const tiers = readTiers(this);
        if (!(text instanceof HTMLElement) || quantity === undefined || tiers.length === 0)
            return;
        // Highest tier first: the first one the quantity reaches. Below every tier, the lowest one.
        const tier = tiers.find((candidate) => quantity >= candidate.quantity) ?? tiers[tiers.length - 1];
        if (!tier)
            return;
        const price = document.createElement('bdi');
        price.textContent = tier.price;
        text.replaceChildren(`${this.dataset.atText ?? ''} `, price, `/${this.dataset.eachText ?? ''}`);
    }
}
if (!customElements.get('price-per-item')) {
    customElements.define('price-per-item', PricePerItemComponent);
}
